import "./DashboardPage.scss";
import { FC, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import LayoutComponent from "../../layouts/layoutComponent/LayoutComponent";
import LocalizationContext from "../../providers/localization/LocalizationContext";
import LocalizationContextType from "../../../domain/providers/localization/LocalizationContextType";
import UserContext from "../../providers/user/UserContext";
import UserContextType from "../../../domain/providers/user/UserContextType";
import routes from "../../routes/Routes";
import GraphDataEntity from "../../../domain/entities/GraphDataEntity";
import { BasicDashboardData } from "../../../domain/repositories/DashboardRepository";
import di from "../../../DependencyInjection";
import GetBasicDashboardDataUseCase, { GetBasicDashboardDataUseCaseName } from "../../../domain/use_cases/dashboard/GetBasicDashboardDataUseCase";
import { isLeft } from "fp-ts/lib/Either";

// Intentar importar Chart.js - fallback si no está disponible
let ChartJS: any = null;
let Line: any = null;

try {
    const chartModule = require('chart.js');
    const reactChartModule = require('react-chartjs-2');
    
    ChartJS = chartModule.Chart;
    Line = reactChartModule.Line;
    
    if (ChartJS) {
        ChartJS.register(
            chartModule.CategoryScale,
            chartModule.LinearScale,
            chartModule.PointElement,
            chartModule.LineElement,
            chartModule.Title,
            chartModule.Tooltip,
            chartModule.Legend
        );
    }
} catch (error) {
    console.warn('Chart.js no está disponible, usando fallback simple');
}

const DashboardPage: FC<{}> = () => {
    const navigate = useNavigate();
    const { i18n } = useContext(LocalizationContext) as LocalizationContextType;
    const { user } = useContext(UserContext) as UserContextType;
    const [graphs, setGraphs] = useState<BasicDashboardData | null | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);

    const _loadDashboardData = async () => {
        setLoading(true);
        try {
            const response = await di.get<GetBasicDashboardDataUseCase>(GetBasicDashboardDataUseCaseName).call();
            
            if (isLeft(response)) {
                console.error("Error al cargar datos del dashboard:", response.left);
                setGraphs(null);
            } else {
                setGraphs(response.right);
            }
        } catch (error) {
            console.error("Error al cargar datos del dashboard:", error);
            setGraphs(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        _loadDashboardData();
    }, []);

    // Función para convertir GraphDataEntity[] a formato Chart.js
    const convertToChartData = (data: GraphDataEntity[], label: string, color: string) => {
        return {
            labels: data.map(item => item.label),
            datasets: [
                {
                    label: label,
                    data: data.map(item => item.value),
                    borderColor: color,
                    backgroundColor: color + '20', // Color con transparencia
                    borderWidth: 2,
                    fill: true,
                    tension: 0.4, // Curva suave
                }
            ]
        };
    };

    // Opciones base para todos los charts
    const chartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top' as const,
            },
            title: {
                display: false,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
            },
        },
    };

    // Componente fallback simple cuando Chart.js no está disponible
    const SimpleChart: FC<{ data: GraphDataEntity[], title: string, color: string }> = ({ data, title, color }) => {
        const maxValue = Math.max(...data.map(item => item.value));
        const minValue = Math.min(...data.map(item => item.value));
        const range = maxValue - minValue;
        const chartHeight = 200;
        const chartWidth = 300;
        
        // Calcular posiciones de los puntos
        const points = data.map((item, index) => {
            const x = (index / (data.length - 1)) * chartWidth;
            const y = chartHeight - ((item.value - minValue) / range) * chartHeight;
            return { x, y, value: item.value, label: item.label };
        });

        // Crear path SVG para la línea
        const linePath = points.map((point, index) => 
            `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
        ).join(' ');

        // Crear path para el área bajo la línea
        const areaPath = `${linePath} L ${chartWidth} ${chartHeight} L 0 ${chartHeight} Z`;
        
        return (
            <div className="simple-line-chart">
                <svg 
                    width="100%" 
                    height={chartHeight + 60} 
                    viewBox={`0 0 ${chartWidth} ${chartHeight + 60}`}
                    preserveAspectRatio="xMidYMid meet"
                >
                    {/* Grid lines */}
                    <defs>
                        <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#f0f0f0" strokeWidth="1"/>
                        </pattern>
                    </defs>
                    <rect width={chartWidth} height={chartHeight} fill="url(#grid)" />
                    
                    {/* Área bajo la línea */}
                    <path 
                        d={areaPath} 
                        fill={color} 
                        fillOpacity="0.2"
                    />
                    
                    {/* Línea principal */}
                    <path 
                        d={linePath} 
                        fill="none" 
                        stroke={color} 
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    
                    {/* Puntos de datos */}
                    {points.map((point, index) => (
                        <g key={index}>
                            <circle 
                                cx={point.x} 
                                cy={point.y} 
                                r="4" 
                                fill={color}
                                stroke="white"
                                strokeWidth="2"
                                className="chart-point"
                            />
                            {/* Etiquetas en el eje X */}
                            <text 
                                x={point.x} 
                                y={chartHeight + 20} 
                                textAnchor="middle" 
                                fontSize="10" 
                                fill="#6c757d"
                                transform={`rotate(-45 ${point.x} ${chartHeight + 20})`}
                            >
                                {point.label}
                            </text>
                            {/* Valores en los puntos */}
                            <text 
                                x={point.x} 
                                y={point.y - 10} 
                                textAnchor="middle" 
                                fontSize="10" 
                                fill="#495057"
                                fontWeight="600"
                                className="chart-value"
                            >
                                {point.value}
                            </text>
                        </g>
                    ))}
                    
                    {/* Eje Y con valores */}
                    {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => {
                        const y = chartHeight - (ratio * chartHeight);
                        const value = Math.round(minValue + (range * ratio));
                        return (
                            <g key={index}>
                                <line 
                                    x1="0" 
                                    y1={y} 
                                    x2={chartWidth} 
                                    y2={y} 
                                    stroke="#e3e6f0" 
                                    strokeWidth="1"
                                    strokeDasharray="2,2"
                                />
                                <text 
                                    x="-5" 
                                    y={y + 3} 
                                    textAnchor="end" 
                                    fontSize="9" 
                                    fill="#6c757d"
                                >
                                    {value}
                                </text>
                            </g>
                        );
                    })}
                </svg>
            </div>
        );
    };

    const quickAccessItems = [
        {
            route: routes.user_list,
            icon: "people",
            title: "Gestión de Usuarios",
            description: "Administrar usuarios del sistema, crear, editar y eliminar cuentas",
            className: "users"
        },
        {
            route: routes.stablishment_list,
            icon: "store",
            title: "Establecimientos",
            description: "Gestionar establecimientos, ubicaciones y configuraciones",
            className: "stablishments"
        },
        {
            route: routes.shift_list,
            icon: "schedule",
            title: "Turnos de Trabajo",
            description: "Programar y administrar turnos de trabajo del personal",
            className: "shifts"
        },
        {
            route: routes.qr_list,
            icon: "qr_code",
            title: "Códigos QR",
            description: "Crear y gestionar códigos QR para establecimientos",
            className: "qrs"
        }
    ];

    const handleNavigate = (path: string) => {
        navigate(path);
    };

    return (
        <div className="dashboard_page">
            <LayoutComponent title={i18n("Dashboard")}>
                <div className="container py-5">
                    {/* Welcome Section */}
                    <div className="welcome_section">
                        <h1 className="welcome_title">
                            {i18n("Bienvenido")}, {user?.name || "Usuario"}
                        </h1>
                        <p className="welcome_subtitle">
                            {i18n("Panel de administración - Selecciona una opción para comenzar")}
                        </p>
                    </div>

                    {/* Quick Access Section */}
                    <div className="quick_access_section">
                        <h2 className="quick_access_title">
                            {i18n("Acceso Rápido")}
                        </h2>
                        <div className="quick_access_grid">
                            {quickAccessItems
                                .filter(item => item.route.auth(user))
                                .map((item, index) => (
                                <div
                                    key={index}
                                    className={`quick_access_card ${item.className}`}
                                    onClick={() => handleNavigate(item.route.relativePath)}
                                >
                                    <div className="card_icon">
                                        <span className="material-symbols-outlined">
                                            {item.icon}
                                        </span>
                                    </div>
                                    <div className="card_title">
                                        {i18n(item.title)}
                                    </div>
                                    <div className="card_description">
                                        {i18n(item.description)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Charts Section */}

                    {/* Stats Section */}
                    {/* <div className="stats_section">
                        <h3 className="stats_title">
                            {i18n("Estadísticas del Sistema")}
                        </h3>
                        <div className="stats_grid">
                            <div className="stat_card">
                                <div className="stat_number">24</div>
                                <div className="stat_label">{i18n("Usuarios Activos")}</div>
                            </div>
                            <div className="stat_card">
                                <div className="stat_number">8</div>
                                <div className="stat_label">{i18n("Establecimientos")}</div>
                            </div>
                            <div className="stat_card">
                                <div className="stat_number">156</div>
                                <div className="stat_label">{i18n("Turnos Este Mes")}</div>
                            </div>
                            <div className="stat_card">
                                <div className="stat_number">42</div>
                                <div className="stat_label">{i18n("QRs Generados")}</div>
                            </div>
                        </div>
                    </div> */}
                </div>
            </LayoutComponent>
        </div>
    );
};

export default DashboardPage;